import { createSelector, Reducer } from '@reduxjs/toolkit';
import { FacetOption } from '@/types/FacetOption';
import { fetchCatalogAdIfNeeded } from './catalogAd/catalogAd.actions';
import { fetchCatalogsIfNeeded } from './catalog/catalog.actions';
import { fetchCatalogsTodayCountApi, fetchResultsApi, SearchAuctionFacet } from '@/redux/api/auctionCalendar';
import { getDeployment } from './config';
import { getUserExcludedHouses } from './searchExclusions';
import { GlobalState } from '../rootReducer';
import { handleActions } from 'redux-actions';
import {
    LOAD_CATALOG_SEARCH_FAIL,
    LOAD_CATALOG_SEARCH_FAIL_ACTION,
    LOAD_CATALOG_SEARCH_REQUEST,
    LOAD_CATALOG_SEARCH_REQUEST_ACTION,
    LOAD_CATALOG_SEARCH_SUCCESS,
    LOAD_CATALOG_SEARCH_SUCCESS_ACTION,
    LOAD_CATALOGS_TODAY_COUNT_FAIL,
    LOAD_CATALOGS_TODAY_COUNT_FAIL_ACTION,
    LOAD_CATALOGS_TODAY_COUNT_REQUEST,
    LOAD_CATALOGS_TODAY_COUNT_REQUEST_ACTION,
    LOAD_CATALOGS_TODAY_COUNT_SUCCESS,
    LOAD_CATALOGS_TODAY_COUNT_SUCCESS_ACTION,
    UPDATE_FACETS_SELECTED,
    UPDATE_FACETS_SELECTED_ACTION,
} from './actions';

export interface State {
    readonly catalogIds: number[];
    readonly catalogsTodayCount: number;
    readonly error: boolean;
    readonly facets: SearchAuctionFacet[];
    readonly submitted: boolean;
    readonly success: boolean;
    readonly totalFound: number;
}

export const DEFAULT_STATE: State = {
    catalogIds: [],
    catalogsTodayCount: 0,
    error: false,
    facets: [],
    submitted: false,
    success: false,
    totalFound: 0,
};

export const reducer: Reducer<State> = handleActions(
    {
        [LOAD_CATALOG_SEARCH_FAIL]: (state: State) => {
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        },
        [LOAD_CATALOG_SEARCH_REQUEST]: (state: State) => {
            return {
                ...state,
                submitted: true,
                success: false,
            };
        },
        [LOAD_CATALOG_SEARCH_SUCCESS]: (state: State, action: LOAD_CATALOG_SEARCH_SUCCESS_ACTION) => {
            return {
                ...state,
                catalogIds: action.payload.results.map((c) => c.catid),
                facets: action.payload.facets,
                submitted: false,
                success: true,
                totalFound: action.payload.count,
            };
        },
        [LOAD_CATALOGS_TODAY_COUNT_FAIL]: (state: State) => {
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        },
        [LOAD_CATALOGS_TODAY_COUNT_REQUEST]: (state: State) => {
            return {
                ...state,
                submitted: true,
                success: false,
            };
        },
        [LOAD_CATALOGS_TODAY_COUNT_SUCCESS]: (state: State, action: LOAD_CATALOGS_TODAY_COUNT_SUCCESS_ACTION) => {
            return {
                ...state,
                catalogsTodayCount: action.payload.count,
                submitted: false,
                success: true,
            };
        },
        [UPDATE_FACETS_SELECTED]: (state: State, action: UPDATE_FACETS_SELECTED_ACTION) => {
            return {
                ...state,
                facets: action.payload.facets,
            };
        },
    },
    DEFAULT_STATE
);

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.auctionCalendar;

export const getUiAuctionCalendar = createSelector(stateSelector, (state) => state);

export const getCatalogsTodayCount = createSelector(stateSelector, (state) => state.catalogsTodayCount);

export const getAuctionCalendarIds = createSelector(stateSelector, (state) => state.catalogIds);

export const getAuctionCalendarTotalCount = createSelector(stateSelector, (state) => state.totalFound);

export const getAuctionCalendarSubmitted = createSelector(stateSelector, (state) => state.submitted);

export const getAuctionCalendarFacets = createSelector(stateSelector, (state) =>
    state.facets.length > 0 ? state.facets : []
);

export const getAuctionCalendarHouseFacets = createSelector(getAuctionCalendarFacets, (facets) => {
    const facet = facets.find((x) => x.id === 'house_id');
    if (Boolean(facet?.values?.length)) {
        return facet.values.sort(sortFacets);
    }
    return [];
});

export const getAuctionCalendarCountryFacets = createSelector(getAuctionCalendarFacets, (facets) => {
    const facet = facets.find((x) => x.id === 'country');
    if (Boolean(facet?.values?.length)) {
        return facet.values.sort(sortFacets);
    }
    return [];
});

export const getAuctionCalendarLiveBroadcastFacets = createSelector(getAuctionCalendarFacets, (facets) => {
    const facet = facets.find((x) => x.id === 'audioVideo');
    if (Boolean(facet?.values?.length)) {
        return facet.values.sort(sortFacets);
    }
    return [];
});

export const getAuctionCalendarShippingFacets = createSelector(getAuctionCalendarFacets, (facets) => {
    const facet = facets.find((x) => x.id === 'shipping');
    if (Boolean(facet?.values?.length)) {
        return facet.values.sort(sortFacets);
    }
    return [];
});

export const getAuctionCalendarSaleTypeFacets = createSelector(getAuctionCalendarFacets, (facets) => {
    const facet = facets.find((x) => x.id === 'sale_type');
    if (Boolean(facet?.values?.length)) {
        return facet.values.sort(sortFacets);
    }
    return [];
});

export const getAuctionCalendarTopRated = createSelector(getAuctionCalendarFacets, (facets) => {
    const topRated = facets.find((x) => x.id === 'topRated');
    // This is necessary as to not add to much logic in the MultiCheckFacet Component.
    const updatedTopRated: FacetOption = {
        count: Boolean(topRated) ? topRated.total_count : 0,
        id: topRated?.id,
        name: topRated?.name,
        selected: false,
    };
    return Boolean(topRated) ? [updatedTopRated] : [];
});

const sortFacets = (a, b) => {
    if (a.selected === b.selected) {
        return b.count - a.count;
    }
    return b.selected ? 1 : -1;
};

/* ACTIONS CREATORS */
export const fetchAuctionCalendarResults = (queryParams: any) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        const searchExclusions = getUserExcludedHouses(state);

        const auctionCalendarQuery = {
            client: 'web',
            client_version: '5.0.0',
            excludedHouses: JSON.stringify(searchExclusions),
            max_facet_values: 3,
            offset: new Date().getTimezoneOffset(),
            pagesize: queryParams.pageSize || queryParams.pagesize,
            ...queryParams,
        };

        dispatch({
            type: LOAD_CATALOG_SEARCH_REQUEST,
        } as LOAD_CATALOG_SEARCH_REQUEST_ACTION);

        const response = await fetchResultsApi({ deployment, queryParams: auctionCalendarQuery });
        dispatch({
            payload: response.payload,
            type: LOAD_CATALOG_SEARCH_SUCCESS,
        } as LOAD_CATALOG_SEARCH_SUCCESS_ACTION);
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_CATALOG_SEARCH_FAIL,
        } as LOAD_CATALOG_SEARCH_FAIL_ACTION);
    }
};

export const fetchCatalogsTodayCountIfNeeded = () => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const todayCount = getCatalogsTodayCount(state);
        if (!todayCount) {
            const deployment = getDeployment(state);

            dispatch({
                type: LOAD_CATALOGS_TODAY_COUNT_REQUEST,
            } as LOAD_CATALOGS_TODAY_COUNT_REQUEST_ACTION);

            const response = await fetchCatalogsTodayCountApi({ deployment });
            dispatch({
                payload: response.data[0],
                type: LOAD_CATALOGS_TODAY_COUNT_SUCCESS,
            } as LOAD_CATALOGS_TODAY_COUNT_SUCCESS_ACTION);
        }
    } catch (error) {
        dispatch({
            error: true,
            type: LOAD_CATALOGS_TODAY_COUNT_FAIL,
        } as LOAD_CATALOGS_TODAY_COUNT_FAIL_ACTION);
    }
};

export const updateFacetsSelected = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const facets = getAuctionCalendarFacets(state);

    dispatch({
        payload: { facets },
        type: UPDATE_FACETS_SELECTED,
    } as UPDATE_FACETS_SELECTED_ACTION);
};

export const fetchDataForAuctionCalendarPage = (queryParams: any) => async (dispatch: Function, getState: Function) => {
    return Promise.all([
        dispatch(fetchCatalogsTodayCountIfNeeded()),
        dispatch(fetchCatalogAdIfNeeded({ pageName: 'calendar' })),
    ])
        .then(() => Promise.all([dispatch(fetchAuctionCalendarResults(queryParams))])) // in order to re-arrange the facets set in state we need to adjust them after the search call
        .then(() => Promise.all([dispatch(updateFacetsSelected())]))
        .then(() => {
            const state = getState();
            const catalogIds = getAuctionCalendarIds(state);
            return Promise.all([dispatch(fetchCatalogsIfNeeded({ catalogIds }))]);
        });
};
